import React, { useCallback } from 'react';

import { useModal } from '@mit/ui-components/right-tower';

import * as style from './composer-club.css';
import { InfoIcon, PlusOutlineIcon } from '../../assets/icons';
import { formatNumber, useIntl, FormattedMessage } from '../../utils';
import { AvailableLabel } from '../available-label';
import { CardButtons } from '../card-buttons';
import { ComposerClubModal } from '../composer-club-modal';

import type { OfferItem } from '../../types';
import type { Bundle } from '@site-builder/store-sdk';

const CC_DECOR_LEFT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/1e5f18fb9dc8bda192e60a35e7525e8c.png@webp';
const CC_DECOR_RIGHT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/cad74fe3095ccc9ab9e8548112ee8399.png@webp';
const MP_DECOR_LEFT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/7b610f462e8ef822b3be4dd7f953a9ce.png@webp';
const MP_DECOR_RIGHT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/276952bf2faa67ad6598ec8faf73149c.png@webp';
const CORNER_TOP_RIGHT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/68840105bd35d3f64b742f86fcbb0d1c.png@webp';
const CORNER_BOTTOM_LEFT =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/0e6aee3f189b2958693f1c3fe808ec83.png@webp';

interface ComposerClubProps {
  readonly data: OfferItem;
}

export const ComposerClub = React.memo(({ data }: ComposerClubProps) => {
  const { isOpen, setIsOpen } = useModal({});
  const infoLabel = useIntl('composer-club.info');
  const blockType = data?.sku || 'composer-club';

  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const renderStepContent = (storeItem: ComposerClubProps['data']) => {
    const bundle = storeItem as Bundle;

    if (bundle.type !== 'bundle') {
      return (
        <div className={style.contentItem}>
          <img
            className={style.contentItemImage}
            src={storeItem.imageUrl ?? ''}
            alt={storeItem.name}
          />
          <span className={style.contentItemAmount}>1</span>
        </div>
      );
    }

    const items = bundle.content;

    const renderItem = ({
      originalItem: item,
      quantity,
    }: (typeof items)[number]) => (
      <div key={item.sku} className={style.contentItem}>
        <img
          className={style.contentItemImage}
          src={item.imageUrl ?? ''}
          alt={item.name}
        />
        <span className={style.contentItemAmount}>
          {formatNumber(quantity)}
        </span>
      </div>
    );

    if (items.length < 5) {
      return <div className={style.content}>{items.map(renderItem)}</div>;
    }

    const [daily, ...rest] = items;
    const weeklyItems = rest.slice(0, 2);
    const monthlyItems = rest.slice(2, 4);

    return (
      <div className={style.contentItemsRow}>
        <div className={style.groupImageWrapper}>
          <span className={style.groupLabel}>
            <FormattedMessage id='composer-club.daily' />
          </span>
          <div className={style.groupImagesRow}>{renderItem(daily)}</div>
        </div>

        <PlusOutlineIcon className={style.plusIcon} />

        <div className={style.groupImageWrapper}>
          <span className={style.groupLabel}>
            <FormattedMessage id='composer-club.weekly' />
          </span>
          <div className={style.groupImagesRow}>
            {weeklyItems.map(renderItem)}
          </div>
        </div>

        <PlusOutlineIcon className={style.plusIcon} />

        <div className={style.groupImageWrapper}>
          <span className={style.groupLabel}>
            <FormattedMessage id='composer-club.monthly' />
          </span>
          <div className={style.groupImagesRow}>
            {monthlyItems.map(renderItem)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={style.blockBase} onClick={handleOpenModal}>
        <img className={style.cornerTopRight} src={CORNER_TOP_RIGHT} alt='' />
        <img
          className={style.cornerBottomLeft}
          src={CORNER_BOTTOM_LEFT}
          alt=''
        />
        {blockType === 'composer-club' ? (
          <>
            <img className={style.ccDecorLeft} src={CC_DECOR_LEFT} alt='' />
            <img className={style.ccDecorRight} src={CC_DECOR_RIGHT} alt='' />
          </>
        ) : (
          <>
            <img className={style.mpDecorLeft} src={MP_DECOR_LEFT} alt='' />
            <img className={style.mpDecorRight} src={MP_DECOR_RIGHT} alt='' />
          </>
        )}
        <div className={`${style.blockInner} msm-frame msm-frame--green`}>
          <div className='msm-frame__corners'></div>

          <div className={style.about}>
            <h2
              className={`${style.title} msm-frame__title msm-frame__title--brown`}
            >
              {data.name}
            </h2>

            <button
              className={style.infoIcon}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleOpenModal();
              }}
              aria-label={infoLabel}
            >
              <InfoIcon className={style.infoIconSvg} />
            </button>
          </div>

          <div className={style.contentWrapper}>
            {renderStepContent(data)}
            <CardButtons />
            <AvailableLabel align={'center'} />
          </div>
        </div>
      </div>

      <ComposerClubModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        bundleData={data as Bundle}
      />
    </>
  );
});

ComposerClub.displayName = 'ComposerClub';
